import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MessageSquare, Pin, Plus, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Discussion {
  id: string;
  title: string;
  content: string;
  user_id: string;
  pinned: boolean;
  created_at: string;
  profiles?: { first_name: string; last_name: string };
  reply_count?: number;
}

interface Reply {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  profiles?: { first_name: string; last_name: string };
}

export function GroupDiscussionBoard({ groupId }: { groupId: string }) {
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [replies, setReplies] = useState<Record<string, Reply[]>>({});
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null);

  useEffect(() => {
    loadDiscussions();
  }, [groupId]);

  const loadDiscussions = async () => {
    setLoading(true);
    try {
      const { data: discussionsData, error } = await supabase
        .from('group_discussions')
        .select('*')
        .eq('group_id', groupId)
        .order('pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles separately
      const userIds = discussionsData?.map(d => d.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]));

      // Get reply counts
      const discussionsWithCounts = await Promise.all(
        (discussionsData || []).map(async (discussion) => {
          const { count } = await supabase
            .from('discussion_replies')
            .select('*', { count: 'exact', head: true })
            .eq('discussion_id', discussion.id);
          
          return { 
            ...discussion, 
            reply_count: count || 0,
            profiles: profileMap.get(discussion.user_id)
          };
        })
      );

      setDiscussions(discussionsWithCounts);
    } catch (error) {
      console.error('Error loading discussions:', error);
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const loadReplies = async (discussionId: string) => {
    try {
      const { data: repliesData, error } = await supabase
        .from('discussion_replies')
        .select('*')
        .eq('discussion_id', discussionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch profiles separately
      const userIds = repliesData?.map(r => r.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]));
      const enriched = repliesData?.map(r => ({
        ...r,
        profiles: profileMap.get(r.user_id)
      })) || [];

      setReplies(prev => ({ ...prev, [discussionId]: enriched }));
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  const createDiscussion = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;

    try {
      const { error } = await supabase
        .from('group_discussions')
        .insert({
          group_id: groupId,
          user_id: user?.id,
          title: newTitle,
          content: newContent,
        });

      if (error) throw error;

      // Log activity
      await supabase.from('group_activities').insert({
        group_id: groupId,
        user_id: user?.id,
        activity_type: 'discussion_created',
        activity_data: { title: newTitle }
      });

      toast.success('Discussion created!');
      setNewTitle('');
      setNewContent('');
      loadDiscussions();
    } catch (error) {
      console.error('Error creating discussion:', error);
      toast.error('Failed to create discussion');
    }
  };

  const createReply = async (discussionId: string) => {
    if (!replyContent.trim()) return;

    try {
      const { error } = await supabase
        .from('discussion_replies')
        .insert({
          discussion_id: discussionId,
          user_id: user?.id,
          content: replyContent,
        });

      if (error) throw error;

      toast.success('Reply posted!');
      setReplyContent('');
      loadReplies(discussionId);
      loadDiscussions(); // Refresh to update reply count
    } catch (error) {
      console.error('Error creating reply:', error);
      toast.error('Failed to post reply');
    }
  };

  const openDiscussion = (discussion: Discussion) => {
    setSelectedDiscussion(discussion);
    loadReplies(discussion.id);
  };

  return (
    <div className="space-y-4">
      {/* Create Discussion */}
      <Dialog>
        <DialogTrigger asChild>
          <Button className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            New Discussion
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start a Discussion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Discussion title..."
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <Textarea
              placeholder="Share your thoughts..."
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              rows={5}
            />
            <Button onClick={createDiscussion} className="w-full">
              Create Discussion
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discussion List */}
      <ScrollArea className="h-[500px]">
        <div className="space-y-3">
          {discussions.map((discussion) => (
            <Card 
              key={discussion.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openDiscussion(discussion)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {discussion.pinned && <Pin className="h-4 w-4 text-primary" />}
                      <h4 className="font-medium">{discussion.title}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {discussion.content}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {discussion.profiles?.first_name} {discussion.profiles?.last_name} • {format(new Date(discussion.created_at), 'PPp')}
                  </span>
                  <Badge variant="outline">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    {discussion.reply_count || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* Discussion Detail Dialog */}
      <Dialog open={!!selectedDiscussion} onOpenChange={() => setSelectedDiscussion(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{selectedDiscussion?.title}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm">{selectedDiscussion?.content}</p>
                <div className="text-xs text-muted-foreground mt-2">
                  Posted by {selectedDiscussion?.profiles?.first_name} {selectedDiscussion?.profiles?.last_name}
                </div>
              </div>

              {/* Replies */}
              <div className="space-y-3">
                {replies[selectedDiscussion?.id || '']?.map((reply) => (
                  <div key={reply.id} className="pl-4 border-l-2 border-muted">
                    <p className="text-sm">{reply.content}</p>
                    <div className="text-xs text-muted-foreground mt-1">
                      {reply.profiles?.first_name} {reply.profiles?.last_name} • {format(new Date(reply.created_at), 'PPp')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              <div className="flex gap-2 pt-4 border-t">
                <Textarea
                  placeholder="Write a reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  rows={2}
                  className="flex-1"
                />
                <Button 
                  size="sm" 
                  onClick={() => selectedDiscussion && createReply(selectedDiscussion.id)}
                  disabled={!replyContent.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}